import React, { useState } from 'react';
import { FeedbackOption } from '../feedback-config';
import { ReactComponent as CloseCrossWhiteIcon } from '/src/assets/svg/icons/close-cross-white.svg';

interface MultiSelectDropdownProps {
  options: readonly FeedbackOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
}

const toggle = (value: string[], key: string): string[] =>
  value.includes(key) ? value.filter((v) => v !== key) : [...value, key];

/**
 * Mobile multi-select: a collapsible checkbox dropdown. Selected options are
 * shown below as removable chips in a horizontally-scrollable, no-wrap row.
 */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Bitte auswählen',
}) => {
  const [open, setOpen] = useState(false);
  const labelFor = (key: string) =>
    options.find((o) => o.value === key)?.label ?? key;

  return (
    <div>
      <button
        type='button'
        aria-expanded={open}
        data-testid='category-dropdown-toggle'
        onClick={() => setOpen((prev) => !prev)}
        className='flex w-full items-center justify-between rounded-md border border-gray-300 px-4! py-3! text-sm! text-(--vel-color-secondary-400)'
      >
        <span>{value.length ? `${value.length} ausgewählt` : placeholder}</span>
        <span
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden='true'
        >
          ▾
        </span>
      </button>

      {open && (
        <ul className='mt-2! rounded-md border border-gray-200 py-1!'>
          {options.map((option) => {
            const selected = value.includes(option.value);
            return (
              <li key={option.value}>
                <label className='grid w-full cursor-pointer grid-cols-[10%_90%] items-center px-4! py-2! text-left text-sm! text-(--vel-color-secondary-400)'>
                  <input
                    type='checkbox'
                    checked={selected}
                    onChange={() => onChange(toggle(value, option.value))}
                    data-testid={`category-checkbox-${option.value}`}
                    className='m-0 justify-self-start align-middle'
                  />
                  <span className='leading-5'>{option.label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {value.length > 0 && (
        <div className='mt-3! flex flex-col flex-nowrap gap-1 overflow-x-auto'>
          {value.map((key) => (
            <span
              key={key}
              data-testid={`selected-chip-${key}`}
              onClick={() => onChange(toggle(value, key))}
              className='flex shrink-0 items-center gap-1 rounded-full border-(--ayvens-theme-brand-primary-400) bg-(--ayvens-theme-brand-primary-400) text-white px-3! py-1! text-xs!'
            >
                            <button
                type='button'
                aria-label={`${labelFor(key)} entfernen`}
                data-testid={`remove-chip-${key}`}
                className='leading-none border-(--ayvens-theme-brand-primary-400) bg-(--ayvens-theme-brand-primary-400) text-white'
              >
                <CloseCrossWhiteIcon width='9.77px' height='9.77px' className='text-white' />
              </button>

              {labelFor(key)}

            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
